import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import type { AuthUser } from "../types/cloud";
import { getMe, ApiError, refreshToken, logout as logoutApi } from "../lib/api/cloud";

interface CloudAuthState {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  refresh: () => Promise<void>;
  logout: () => void;
}

const CloudAuthContext = createContext<CloudAuthState | null>(null);

export const CloudAuthProvider = CloudAuthContext.Provider;

/** Shared ref — when true, the KeycloakAuthGate must not redirect to login. */
export const loggingOutRef = { current: false };

/** Minimum time before expiry to trigger a proactive refresh (ms). */
const REFRESH_MARGIN_MS = 60_000;

/** Decode the `exp` claim from a JWT without verifying the signature. */
function getTokenExpiry(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload: { exp?: number } = JSON.parse(atob(parts[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function useCloudAuthState(): CloudAuthState {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Schedule a proactive token refresh based on the current access token's expiry. */
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const token = localStorage.getItem("cloud_access_token");
    if (!token) return;

    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    const delay = Math.max(expiry - Date.now() - REFRESH_MARGIN_MS, 0);

    refreshTimerRef.current = setTimeout(async () => {
      const result = await refreshToken();
      if (result) {
        // Token was refreshed — schedule the next refresh
        scheduleRefresh();
      }
      // If refresh failed, the next API call will get a 401 and the
      // interceptor will handle redirect to login.
    }, delay);
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("cloud_access_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const me = await getMe();
      setUser(me);
      setError(null);
      scheduleRefresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        localStorage.removeItem("cloud_access_token");
        setUser(null);
      } else {
        setError(err instanceof Error ? err.message : "Failed to fetch user");
      }
    } finally {
      setLoading(false);
    }
  }, [scheduleRefresh]);

  const logout = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    localStorage.removeItem("cloud_access_token");

    // Mark that we are logging out — prevents KeycloakAuthGate from
    // immediately redirecting to login while the browser navigates away.
    loggingOutRef.current = true;

    // Call backend to clear cookies and get Keycloak logout URL,
    // then redirect the browser to terminate the SSO session.
    logoutApi().then((result) => {
      if (result?.logoutUrl) {
        window.location.href = result.logoutUrl;
      } else {
        // Fallback: if backend logout failed, just clear state
        setUser(null);
        loggingOutRef.current = false;
      }
    }).catch(() => {
      setUser(null);
      loggingOutRef.current = false;
    });
  }, []);

  useEffect(() => {
    // Reset loggingOutRef on mount — if a previous logout was interrupted
    // (e.g. React strict mode remount), this ensures future logouts work.
    loggingOutRef.current = false;

    refresh();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [refresh]);

  return {
    user,
    loading,
    error,
    isAuthenticated: user !== null,
    isAdmin: user?.role === "admin",
    refresh,
    logout,
  };
}

export function useCloudAuth(): CloudAuthState {
  const context = useContext(CloudAuthContext);
  if (!context) {
    throw new Error("useCloudAuth must be used within a CloudAuthProvider");
  }
  return context;
}
